import { Category } from 'apis/entities/category.entity'
import {
  DocumentUser,
  Document,
  DocumentCheckerResponse,
} from 'apis/entities/document.entity'
import { Image } from 'apis/entities/image.entity'
import { Tag } from 'apis/entities/tag.entity'
import { UploadImage } from 'apis/entities/upload-image.entity'
import { useContext, ReactNode, createContext, useRef } from 'react'
import axios from 'axios'
import { UploadFile } from 'apis/entities/upload-file.entity'
import { GeneratedQuestion } from 'apis/entities/quiz.entity'
import { BaseResponse } from 'apis/entities/base-response.entity'
import { ToastUtil } from 'utils/ToastUtil'

type Props = {
  children: ReactNode
}

interface ApiValue {
  setToken: (token: string | null) => void
  publish(documentId: string): Promise<any>
  upload(body: FormData): Promise<any>
  uploadGrades(body: any): Promise<any>
  createTag(name: string): Promise<Tag>
  getTags(): Promise<Tag[]>
  getTagsByQuery(query: string): Promise<Tag[]>
  updateTag(id: string, name: string): Promise<boolean>
  deleteTag(id: string): Promise<boolean>
  createImage(
    name: string,
    url: string,
    size: number,
    tagNames?: string,
    tagIds?: string[],
    description?: string,
    categoryId?: string,
    subcategoryIds?: string[],
  ): Promise<Image>
  updateImage(
    id: string,
    tagIds?: string[],
    name?: string,
    description?: string,
  ): Promise<Image>
  bulkUpdateImages(
    imageIds: string[],
    tagIds?: string[],
    description?: string | undefined | null,
  ): Promise<boolean>
  deleteImage(id: string): Promise<boolean>
  getImagesBySearch(
    page: number,
    size: number,
    tagNames?: string,
    tagIds?: string[],
    categoryIds?: string[],
    subcategoryIds?: string[],
    description?: string,
    excludeBackgroundImages?: boolean,
  ): Promise<[Image[], number]>
  deleteImagesByTagIds(tagId: string, imageIds: string[]): Promise<boolean>
  deleteImages(imageIds: string[]): Promise<boolean>
  reassignTags(imageIds: string[], tagIds: string[]): Promise<boolean>
  reassign(
    imageIds: string[],
    props: {
      tagIds: string[]
      subcategoryIds: string[]
      description?: string
    },
  ): Promise<boolean>
  uploadImage(
    file: File,
    tagNames?: string,
    tagIds?: string[],
    description?: string,
    categoryId?: string,
    subcategoryIds?: string[],
  ): Promise<string | null>
  uploadFile(file: File): Promise<string | null>
  getCanvasAssignments(clientConnId: string, courseId: string): Promise<any>
  getCanvasClients(): Promise<any>
  getCanvasCourses(clientConnId: string): Promise<any>
  getCanvasMarkingSheet(
    courseId: string,
    assignmentId: string,
    clientId: string,
  ): Promise<any>
  getDocuments(
    filter?: string,
    search?: string,
    dateStart?: Date,
    dateEnd?: Date,
  ): Promise<any>
  getDocumentsV2(
    filter?: string,
    search?: string,
    dateStart?: Date,
    dateEnd?: Date,
    pageSize?: number,
    pageNumber?: number,
  ): Promise<any>
  getDocument(documentId: string): Promise<Document>
  getDocumentHtml(documentId: string): Promise<string>
  getDocumentPublishedBy(): Promise<DocumentUser[]>
  deleteDocument(documentId: string): Promise<any>
  downloadDocument(documentId: string): Promise<any>
  downloadDocumentHtml(documentId: string, name: string): Promise<void>
  downloadIntV1AsWord(documentId: string, name: string): Promise<void>
  getDocumentIntV1(documentId: string): Promise<any>
  viewIntV1Json(documentId: string): Promise<void>
  getPrintPdfPresignedUrl(documentId: string): Promise<{ presignedUrl: string }>
  checkPrintPdfExists(documentId: string): Promise<{ exists: boolean }>
  checkPrintPdfExistsBatch(
    documentIds: string[],
  ): Promise<Record<string, boolean>>
  downloadPrintPdf(documentId: string): Promise<Blob>
  updateDocument(documentId: string, modifications: object): Promise<any>
  updateDocumentByGeneratedQuestions(
    documentId: string,
    generatedQuestions: GeneratedQuestion[],
  ): Promise<Document>
  getLxFiles(
    filter?: string,
    search?: string,
    dateStart?: Date,
    dateEnd?: Date,
  ): Promise<any>
  getRecentlyCreatedFiles(limit?: number): Promise<
    Record<
      string,
      {
        id: string
        name: string
        filename: string
        fileExt: string
        createdAt: string
        fileUrl: string
      }[]
    >
  >
  getLxFilePublishedBy(): Promise<DocumentUser[]>
  generateLxFileEmbedding(fileId: string): Promise<any>
  deleteLxFile(fileId: string): Promise<any>
  updateLxFile(fileId: string, modifications: object): Promise<any>
  uploadLxFile(body: any, onProgress: any): Promise<any>
  getImageCategories(): Promise<Category[]>
  documentChecker(docId: string): Promise<DocumentCheckerResponse>
  setImageCategory(
    categoryId: string,
    imageIds: string[],
  ): Promise<BaseResponse>
  generateQuestions(documentId: string, nQuestions?: number): Promise<any>
}

export const ApiContext = createContext<ApiValue | null>(null)

export function useApi(): ApiValue {
  const state = useContext(ApiContext)
  if (!state) {
    throw new Error('useApi must be used within ApiProvider')
  }
  return state
}

export function ApiProvider({ children }: Props) {
  const headers = { 'Content-Type': 'application/json' }
  const refToken = useRef<string | null>(null)
  const refHeaders = useRef<any>(headers)
  const documentHost = process.env.REACT_APP_DOCUMENT_API_URL
  const authoringToolHost = process.env.REACT_APP_AUTHORING_TOOL_API_URL

  const setToken = (token: string | null) => {
    refToken.current = token
    refHeaders.current = {
      ...headers,
      Authorization: `Bearer ${token}`,
    }
  }

  const publish = async (documentId: string): Promise<any> => {
    const api = `${authoringToolHost}/documents/${documentId}/publish`
    const res = await fetch(api, {
      method: 'GET',
      headers: refHeaders.current,
    })
    return res.json()
  }

  const upload = async (body: any): Promise<any> => {
    const api = `${authoringToolHost}/documents/upload`
    const res = await fetch(api, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${refToken.current}`,
      },
      body: body,
    })
    const data = await res.json()
    if (res.status >= 400) {
      throw data
    }
    return data
  }

  const documentChecker = async (
    documentId: string,
  ): Promise<DocumentCheckerResponse> => {
    const res = await fetch(
      `${authoringToolHost}/documents/${documentId}/checker`,
      {
        method: 'GET',
        headers: refHeaders.current,
      },
    )
    return res.json()
  }

  const downloadDocument = async (documentId: string): Promise<any> => {
    const api = `${authoringToolHost}/documents/${documentId}/download`
    const res = await fetch(api, {
      method: 'GET',
      headers: refHeaders.current,
    })
    return res.blob()
  }

  // A failed request still has a body (usually
  // {"statusCode":...,"message":"..."}) — without checking res.ok first,
  // that error body gets silently saved as if it were the real file.
  const extractErrorMessage = async (res: Response): Promise<string> => {
    try {
      const data = await res.clone().json()
      return data?.message || `Request failed (HTTP ${res.status})`
    } catch {
      return `Request failed (HTTP ${res.status})`
    }
  }

  const downloadBlob = (blob: Blob, filename: string): void => {
    const blobURL = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = blobURL
    link.download = filename
    link.click()
    URL.revokeObjectURL(blobURL)
  }

  const downloadDocumentHtml = async (
    documentId: string,
    name: string,
  ): Promise<void> => {
    const api = `${documentHost}/documents/${documentId}/html`
    const res = await fetch(api, { method: 'GET', headers: refHeaders.current })
    if (!res.ok) {
      ToastUtil.error(`Failed to download html: ${await extractErrorMessage(res)}`)
      return
    }
    downloadBlob(await res.blob(), `${name || documentId}.html`)
  }

  const downloadIntV1AsWord = async (
    documentId: string,
    name: string,
  ): Promise<void> => {
    const api = `${authoringToolHost}/documents/${documentId}/int-v1-to-word`
    const res = await fetch(api, { method: 'GET', headers: refHeaders.current })
    if (!res.ok) {
      ToastUtil.error(`intV1 -> word failed: ${await extractErrorMessage(res)}`)
      return
    }
    downloadBlob(await res.blob(), `${name || documentId}.int-v1.docx`)
  }

  const getPrintPdfPresignedUrl = async (
    documentId: string,
  ): Promise<{ presignedUrl: string }> => {
    const api = `${documentHost}/documents/${documentId}/print-pdf-presigned-url`
    const res = await fetch(api, { method: 'GET', headers: refHeaders.current })
    if (!res.ok) {
      throw new Error(`Failed to get presigned URL: ${res.status}`)
    }
    return res.json()
  }

  const checkPrintPdfExists = async (
    documentId: string,
  ): Promise<{ exists: boolean }> => {
    const api = `${documentHost}/documents/${documentId}/print-pdf-exists`
    const res = await fetch(api, { method: 'GET', headers: refHeaders.current })
    if (!res.ok) {
      throw new Error(`Failed to check print.pdf: ${res.status}`)
    }
    return res.json()
  }

  // batched version of checkPrintPdfExists — checks all ids in one
  // request instead of one per topic, which otherwise serialises behind the
  // browser's per-origin connection limit on courses with many topics.
  const checkPrintPdfExistsBatch = async (
    documentIds: string[],
  ): Promise<Record<string, boolean>> => {
    const api = `${documentHost}/documents/print-pdf-exists-batch`
    const res = await fetch(api, {
      method: 'POST',
      headers: refHeaders.current,
      body: JSON.stringify({ ids: documentIds }),
    })
    if (!res.ok) {
      throw new Error(`Failed to check print.pdf batch: ${res.status}`)
    }
    return res.json()
  }

  const downloadPrintPdf = async (documentId: string): Promise<Blob> => {
    const api = `${documentHost}/documents/${documentId}/print-pdf`
    const res = await fetch(api, { method: 'GET', headers: refHeaders.current })
    if (!res.ok) {
      throw new Error(`Failed to download print.pdf: ${res.status}`)
    }
    return res.blob()
  }

  const updateDocumentByGeneratedQuestions = async (
    documentId: string,
    generatedQuestions: GeneratedQuestion[],
  ): Promise<Document> => {
    const api = `${authoringToolHost}/documents/${documentId}/regenerate-questions`
    const res = await fetch(api, {
      method: 'PATCH',
      headers: refHeaders.current,
      body: JSON.stringify({ generatedQuestions: generatedQuestions }),
    })
    return res.json()
  }

  // for authoring tool, it is cloned from QuizProvider.tsx
  const generateQuestions = async (
    documentId: string,
    nQuestions: number = 1,
  ): Promise<any> => {
    const api = `${authoringToolHost}/gpt/generate-questions`
    const res = await fetch(api, {
      method: 'POST',
      headers: refHeaders.current,
      body: JSON.stringify({
        // id: documentId, using by hlp-api
        docId: documentId,
        nQuestions: nQuestions,
      }),
    })
    const data = await res.json()
    if (res.status >= 400) {
      throw data
    }
    return data
  }

  const uploadGrades = async (body: any): Promise<any> => {
    const api = `${documentHost}/canvas-lms/upload-grades`
    const res = await fetch(api, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${refToken.current}`,
      },
      body: body,
    })
    const data = await res.json()
    if (res.status >= 400) {
      throw data
    }
    return data
  }

  const getCanvasAssignments = async (
    clientConnId: string,
    courseId: string,
  ): Promise<any> => {
    const api = `${documentHost}/canvas-lms/assignments/${clientConnId}/${courseId}`
    const res = await fetch(api, {
      method: 'GET',
      headers: refHeaders.current,
    })
    const data = await res.json()
    if (data.assignments) {
      return data.assignments
    } else {
      throw res
    }
  }
  const getCanvasClients = async (): Promise<any> => {
    const res = await fetch(`${documentHost}/canvas-lms/clients`, {
      method: 'GET',
      headers: refHeaders.current,
    })
    const data = await res.json()
    if (data.clients) {
      return data.clients
    } else {
      throw res
    }
  }
  const getCanvasCourses = async (clientConnId: string): Promise<any> => {
    try {
      const res = await fetch(
        `${documentHost}/canvas-lms/courses/${clientConnId}`,
        {
          method: 'GET',
          headers: refHeaders.current,
        },
      )
      const data = await res.json()
      if (data.courses) {
        return data.courses
      } else {
        throw res
      }
    } catch (err) {
      console.error(err)
      return []
    }
  }
  const getCanvasMarkingSheet = async (
    courseId: string,
    assignmentId: string,
    clientId: string,
  ): Promise<any> => {
    const res = await fetch(`${documentHost}/canvas-lms/export`, {
      method: 'POST',
      headers: refHeaders.current,
      body: JSON.stringify({
        courseId,
        assignmentId,
        clientId,
      }),
    })
    return res.blob()
  }
  const getDocumentPublishedBy = async (): Promise<DocumentUser[]> => {
    try {
      const api = `${documentHost}/documents/find/published-by`
      const res = await fetch(api, {
        method: 'GET',
        headers: refHeaders.current,
      })
      const result = await res.json()
      if (res.status >= 400) {
        throw result
      }
      const users = result as DocumentUser[]
      return users
    } catch (err) {
      console.error(err)
      return []
    }
  }

  const getDocuments = async (
    filter?: string,
    search?: string,
    dateStart?: Date,
    dateEnd?: Date,
  ): Promise<any> => {
    let api = `${documentHost}/documents/find?${new URLSearchParams({
      ...(filter && { filter }),
      ...(search && { search }),
      ...(dateStart && { dateStart: dateStart.toISOString() }),
      ...(dateEnd && { dateEnd: dateEnd.toISOString() }),
    })}`
    try {
      const res = await fetch(api, {
        method: 'GET',
        headers: refHeaders.current,
      })
      return res.json()
    } catch (err) {
      console.error(err)
      return []
    }
  }

  const getDocumentsV2 = async (
    filter?: string,
    search?: string,
    dateStart?: Date,
    dateEnd?: Date,
    pageSize?: number,
    pageNumber?: number,
  ): Promise<any> => {
    let api = `${documentHost}/documents/find-v2?${new URLSearchParams({
      ...(filter && { filter }),
      ...(search && { search }),
      ...(dateStart && { dateStart: dateStart.toISOString() }),
      ...(dateEnd && { dateEnd: dateEnd.toISOString() }),
      ...(pageSize && { pageSize: pageSize.toString() }),
      ...(pageNumber && { pageNumber: pageNumber.toString() }),
    })}`
    try {
      const res = await fetch(api, {
        method: 'GET',
        headers: refHeaders.current,
      })
      return res.json()
    } catch (err) {
      console.error(err)
      return {
        page: 1,
        pageSize: 10,
        items: [],
        totalItems: 0,
      }
    }
  }

  const getDocument = async (documentId: string): Promise<Document> => {
    // minimal data
    const api = `${documentHost}/documents/${documentId}/topics`
    const res = await fetch(api, {
      method: 'GET',
      headers: refHeaders.current,
    })
    return res.json()
  }

  // full document (unlike getDocument above, which only fetches minimal
  // id+topics data) — used to grab outputHtml for the "Copy HTML" actions.
  const getDocumentHtml = async (documentId: string): Promise<string> => {
    const api = `${documentHost}/documents/${documentId}`
    const res = await fetch(api, {
      method: 'GET',
      headers: refHeaders.current,
    })
    const data = await res.json()
    return data.outputHtml ?? ''
  }

  // Debug: grabs the raw intV1 blob off the full document (lx-engine-backend
  // and authoring-tool-backend share the same lx_engine.document table/
  // column, so either host would do — reusing documentHost here since
  // getDocumentHtml already fetches the same full-document shape from it).
  const getDocumentIntV1 = async (documentId: string): Promise<any> => {
    const api = `${documentHost}/documents/${documentId}`
    const res = await fetch(api, {
      method: 'GET',
      headers: refHeaders.current,
    })
    if (!res.ok) {
      throw new Error(await extractErrorMessage(res))
    }
    const data = await res.json()
    return data.intV1 ?? null
  }

  const viewIntV1Json = async (documentId: string): Promise<void> => {
    let intV1: any
    try {
      intV1 = await getDocumentIntV1(documentId)
    } catch (err: any) {
      ToastUtil.error(`Failed to load intV1: ${err.message}`)
      return
    }
    if (intV1 === null) {
      ToastUtil.warning(`Document ${documentId} has no intV1 (not backfilled yet)`)
      return
    }
    const blob = new Blob([JSON.stringify(intV1, null, 2)], {
      type: 'application/json',
    })
    const blobURL = URL.createObjectURL(blob)
    window.open(blobURL, '_blank')
  }

  const deleteDocument = async (documentId: string): Promise<any> => {
    const api = `${documentHost}/documents/${documentId}`
    const res = await fetch(api, {
      method: 'DELETE',
      headers: refHeaders.current,
    })
    return res.json()
  }

  const updateDocument = async (
    documentId: string,
    modifications: object,
  ): Promise<any> => {
    const api = `${documentHost}/documents/${documentId}`
    const res = await fetch(api, {
      method: 'PATCH',
      headers: refHeaders.current,
      body: JSON.stringify(modifications),
    })
    if (res.status >= 400) {
      throw await res.json()
    }
    return res.json()
  }

  const uploadFile = async (file: File): Promise<string | null> => {
    try {
      const filename = file.name
      const data = await getPresignedUrl(filename)
      if (data.presigned_url) {
        const result = await putPresignedUrl(data.presigned_url, file)
        if (result) {
          return data.url
        }
      }
    } catch (err) {
      console.error(err)
    }
    return null
  }

  const uploadImage = async (
    file: File,
    tagNames?: string,
    tagIds?: string[],
    description?: string,
    categoryId?: string,
    subcategoryIds?: string[],
  ): Promise<string | null> => {
    try {
      const filename = file.name
      const data = await getPresignedUrlForImages(filename)
      const result = await putPresignedUrl(data.presigned_url, file)
      if (result) {
        const image = await createImage(
          filename,
          data.url,
          file.size,
          tagNames,
          tagIds,
          description,
          categoryId,
          subcategoryIds,
        )
        console.log(image)

        return data.url // same as image.url
      }
    } catch (err) {
      console.error(err)
    }
    return null
  }

  const getPresignedUrlForImages = async (
    filename: string,
  ): Promise<UploadImage> => {
    const body = { filename: filename }
    const api = `${documentHost}/upload/images`
    const res = await fetch(api, {
      method: 'POST',
      headers: refHeaders.current,
      body: JSON.stringify(body),
    })
    return await res.json()
  }

  const getPresignedUrl = async (filename: string): Promise<UploadFile> => {
    const body = { filename: filename }
    const api = `${documentHost}/upload`
    const res = await fetch(api, {
      method: 'POST',
      headers: refHeaders.current,
      body: JSON.stringify(body),
    })
    return await res.json()
  }

  const putPresignedUrl = async (
    presignedUrl: string,
    file: File,
  ): Promise<boolean> => {
    // if add header, it becomes downloadable file instead of viewing on browser
    // const headers = { 'Content-Type': 'application/octet-stream' }
    const res = await fetch(presignedUrl, {
      method: 'PUT',
      // headers: refHeaders.current,
      body: file,
    })
    return res.status === 200
  }

  const createTag = async (name: string): Promise<Tag> => {
    const body = { name: name }
    const api = `${documentHost}/tags`
    const res = await fetch(api, {
      method: 'POST',
      headers: refHeaders.current,
      body: JSON.stringify(body),
    })
    return await res.json()
  }

  const getTags = async (): Promise<Tag[]> => {
    const api = `${documentHost}/tags`
    const res = await fetch(api, {
      method: 'GET',
      headers: refHeaders.current,
    })
    const data = await res.json()
    return data['tags']
  }

  const getTagsByQuery = async (query: string): Promise<Tag[]> => {
    const api = `${documentHost}/tags/search?q=${query}`
    const res = await fetch(api, {
      method: 'GET',
      headers: refHeaders.current,
    })
    const data = await res.json()
    return data['tags']
  }

  const updateTag = async (id: string, name: string): Promise<boolean> => {
    const api = `${documentHost}/tags/${id}`
    const res = await fetch(api, {
      method: 'PATCH',
      headers: refHeaders.current,
      body: JSON.stringify({ name: name }),
    })
    const data = await res.json()
    return data.affected > 0
  }

  const deleteTag = async (id: string): Promise<boolean> => {
    const api = `${documentHost}/tags/${id}`
    const res = await fetch(api, {
      method: 'DELETE',
      headers: refHeaders.current,
    })
    const data = await res.json()
    return data['success']
  }

  const createImage = async (
    name: string,
    url: string,
    size: number,
    tagNames?: string,
    tagIds?: string[],
    description?: string,
    categoryId?: string,
    subcategoryIds?: string[],
  ): Promise<Image> => {
    const body = {
      name: name,
      url: url,
      size: size,
      tagNames: tagNames,
      tagIds: tagIds,
      description: description,
      categoryId: categoryId,
      subcategoryIds: subcategoryIds,
    }
    const api = `${documentHost}/images`
    const res = await fetch(api, {
      method: 'POST',
      headers: refHeaders.current,
      body: JSON.stringify(body),
    })
    return await res.json()
  }

  const updateImage = async (
    id: string,
    tagIds?: string[],
    name?: string,
    description?: string,
  ) => {
    let body: any = {}
    if (tagIds) body['tagIds'] = tagIds
    if (name) body['name'] = name
    if (description) body['description'] = description

    const api = `${documentHost}/images/${id}`
    const res = await fetch(api, {
      method: 'PATCH',
      headers: refHeaders.current,
      body: JSON.stringify(body),
    })
    return await res.json()
  }

  const bulkUpdateImages = async (
    imageIds: string[],
    tagIds?: string[],
    description?: string | undefined | null,
  ): Promise<boolean> => {
    let body: any = {}
    if (imageIds) body['imageIds'] = imageIds
    if (tagIds) body['tagIds'] = tagIds
    if (description !== undefined) body['description'] = description

    try {
      const api = `${documentHost}/images/bulk-update`
      const res = await fetch(api, {
        method: 'POST',
        headers: refHeaders.current,
        body: JSON.stringify(body),
      })
      const result = await res.json()
      return result['success']
    } catch (err) {
      console.error(err)
      return false
    }
  }

  const deleteImage = async (id: string): Promise<boolean> => {
    const api = `${documentHost}/images/${id}`
    try {
      const res = await fetch(api, {
        method: 'DELETE',
        headers: refHeaders.current,
      })
      const data = await res.json()
      return data['success']
    } catch (err) {
      console.error(err)
      return false
    }
  }

  const getImagesBySearch = async (
    page: number,
    size: number,
    tagNames?: string,
    tagIds?: string[],
    categoryIds?: string[],
    subcategoryIds?: string[],
    description?: string,
    excludeBackgroundImages?: boolean,
  ): Promise<[Image[], number]> => {
    const body = {
      page: page,
      size: size,
      tagNames: tagNames,
      tagIds: tagIds,
      categoryIds: categoryIds,
      subcategoryIds: subcategoryIds,
      description: description,
      excludeBackgroundImages: excludeBackgroundImages,
    }
    const api = `${documentHost}/images/search`
    const res = await fetch(api, {
      method: 'POST',
      headers: refHeaders.current,
      body: JSON.stringify(body),
    })
    const data = await res.json()
    return [data['images'], data['nextPage']]
  }

  const deleteImagesByTagIds = async (
    tagId: string,
    imageIds: string[],
  ): Promise<boolean> => {
    try {
      const api = `${documentHost}/tags/${tagId}/delete-images`
      const body = { imageIds: imageIds }
      const res = await fetch(api, {
        method: 'POST',
        headers: refHeaders.current,
        body: JSON.stringify(body),
      })
      const data = await res.json()
      return data['success']
    } catch (err) {
      console.error(err)
      return false
    }
  }

  const deleteImages = async (imageIds: string[]): Promise<boolean> => {
    try {
      const api = `${documentHost}/images/bulk-delete`
      const body = { imageIds: imageIds }
      const res = await fetch(api, {
        method: 'POST',
        headers: refHeaders.current,
        body: JSON.stringify(body),
      })
      const data = await res.json()
      return data['success']
    } catch (err) {
      console.error(err)
      return false
    }
  }

  const reassignTags = async (
    imageIds: string[],
    tagIds: string[],
  ): Promise<boolean> => {
    try {
      const api = `${documentHost}/images/reassign-tags`
      const body = { imageIds: imageIds, tagIds: tagIds }
      const res = await fetch(api, {
        method: 'POST',
        headers: refHeaders.current,
        body: JSON.stringify(body),
      })
      const data = await res.json()
      return data['success']
    } catch (err) {
      console.error(err)
      return false
    }
  }

  const reassign = async (
    imageIds: string[],
    props: {
      tagIds: string[]
      subcategoryIds: string[]
      description?: string
    },
  ): Promise<boolean> => {
    try {
      if (imageIds.length > 0 && props.tagIds.length > 0) {
        await reassignTags(imageIds, props.tagIds)
      }

      const resp1 = await fetch(
        `${documentHost}/images/reassign-subcategories-and-description`,
        {
          method: 'POST',
          headers: refHeaders.current,
          body: JSON.stringify({
            imageIds: imageIds,
            ...(props.subcategoryIds && {
              subcategoryIds: props.subcategoryIds,
            }),
            ...(props.description !== undefined && {
              description: props.description,
            }),
          }),
        },
      )

      const data1 = await resp1.json()

      return data1['success']
    } catch (err) {
      console.error(err)
      return false
    }
  }

  const getImageCategories = async (): Promise<Category[]> => {
    const api = `${documentHost}/categories`
    const res = await fetch(api, {
      method: 'GET',
      headers: refHeaders.current,
    })
    const data = await res.json()
    return data['categories']
  }

  const getLxFiles = async (
    filter?: string,
    search?: string,
    dateStart?: Date,
    dateEnd?: Date,
  ): Promise<any> => {
    let api = `${documentHost}/lx-files/find?${new URLSearchParams({
      ...(filter && { filter }),
      ...(search && { search }),
      ...(dateStart && { dateStart: dateStart.toISOString() }),
      ...(dateEnd && { dateEnd: dateEnd.toISOString() }),
    })}`
    try {
      const res = await fetch(api, {
        method: 'GET',
        headers: refHeaders.current,
      })
      return res.json()
    } catch (err) {
      console.error(err)
      return []
    }
  }

  const getRecentlyCreatedFiles = async (limit = 5) => {
    try {
      const res = await fetch(
        `${documentHost}/lx-files/recently-created?limit=${limit}`,
        {
          method: 'GET',
          headers: refHeaders.current,
        },
      )
      return res.json()
    } catch {
      return {}
    }
  }

  const getLxFilePublishedBy = async (): Promise<DocumentUser[]> => {
    try {
      const api = `${documentHost}/lx-files/find/published-by`
      const res = await fetch(api, {
        method: 'GET',
        headers: refHeaders.current,
      })
      return await res.json()
    } catch (err) {
      console.error(err)
      return []
    }
  }

  const generateLxFileEmbedding = async (fileId: string): Promise<any> => {
    const api = `${documentHost}/lx-files/${fileId}/generate-embedding`
    const res = await fetch(api, {
      method: 'POST',
      headers: refHeaders.current,
    })
    const data = await res.json()
    if (res.status >= 400) {
      throw data
    }
    return data
  }

  const updateLxFile = async (
    documentId: string,
    modifications: object,
  ): Promise<any> => {
    const api = `${documentHost}/lx-files/${documentId}`
    const res = await fetch(api, {
      method: 'PATCH',
      headers: refHeaders.current,
      body: JSON.stringify(modifications),
    })
    const data = await res.json()
    if (res.status >= 400) {
      throw data
    }
    return data
  }

  const deleteLxFile = async (documentId: string): Promise<any> => {
    const api = `${documentHost}/lx-files/${documentId}`
    const res = await fetch(api, {
      method: 'DELETE',
      headers: refHeaders.current,
    })
    const data = await res.json()
    if (res.status >= 400) {
      throw data
    }
    return data
  }

  const uploadLxFile = async (formData: any, onProgress: any): Promise<any> => {
    const formObject = Object.fromEntries(formData.entries())
    const filename = formObject.filenameOverride

    const preResp: any = await axios.post(
      `${documentHost}/lx-files/upload/presigned-url`,
      {
        filename: filename,
        filesize: formObject.file.size,
      },
      { headers: refHeaders.current },
    )
    const presignedUrls = preResp.data.presignedUrls
    if (presignedUrls.length > 1) {
      const chunkSize = Math.ceil(formObject.file.size / presignedUrls.length)
      const etags = []
      for (let i = 0; i < presignedUrls.length; i++) {
        const start = i * chunkSize
        const end = (i + 1) * chunkSize
        const chunk = formObject.file.slice(start, end)

        const resp = await axios.put(presignedUrls[i], chunk, {
          onUploadProgress: (progressEvent) => {
            const base = (i / presignedUrls.length) * 100
            const percentCompleted = progressEvent.total
              ? (100 * progressEvent.loaded) /
                progressEvent.total /
                presignedUrls.length
              : 0
            onProgress && onProgress(Math.round(base + percentCompleted))
          },
          headers: {
            ...(preResp.data.contentType && {
              'Content-Type': preResp.data.contentType,
            }),
          },
        })
        console.log(`Chunk ${i + 1} uploaded:`, resp.headers.etag)
        etags.push(resp.headers.etag)
      }

      await axios.post(
        `${documentHost}/lx-files/upload/multipart-complete`,
        {
          lxFileId: preResp.data.lxFileId,
          filename: filename,
          uploadId: preResp.data.uploadId,
          etags,
        },
        { headers: refHeaders.current },
      )
    } else {
      await axios.put(presignedUrls[0], formObject.file, {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = progressEvent.total
            ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
            : 0
          onProgress && onProgress(percentCompleted)
        },
        headers: {
          ...(preResp.data.contentType && {
            'Content-Type': preResp.data.contentType,
          }),
        },
      })
    }
    // console.log(preResp.data)
    const res = await axios.post(
      `${documentHost}/lx-files/upload`,
      {
        lxFileId: preResp.data.lxFileId,
        auth0Id: formObject.auth0Id,
        userId: formObject.userId,
        filenameOverride: formObject.filenameOverride,
        username: formObject.username,
        description: formObject.description,
        url: preResp.data.url,
        fileSize: formObject.file.size,
        businessUnit: formObject.businessUnit,
      },
      {
        headers: {
          Authorization: `Bearer ${refToken.current}`,
        },
      },
    )
    return res.data
  }

  const setImageCategory = async (
    categoryId: string,
    imageIds: string[],
  ): Promise<BaseResponse> => {
    try {
      const res = await fetch(`${documentHost}/images/change-category`, {
        method: 'POST',
        headers: refHeaders.current,
        body: JSON.stringify({ categoryId: categoryId, imageIds: imageIds }),
      })
      const data = await res.json()
      return data
    } catch (err) {
      console.error(err)
      return {
        success: false,
      }
    }
  }
  const providerValue = {
    setToken,
    publish,
    upload,
    uploadGrades,
    uploadImage,
    updateDocument,
    updateDocumentByGeneratedQuestions,
    getCanvasAssignments,
    getCanvasClients,
    getCanvasCourses,
    getCanvasMarkingSheet,
    getDocuments,
    getDocumentsV2,
    getDocument,
    getDocumentHtml,
    getDocumentPublishedBy,
    deleteDocument,
    downloadDocument,
    downloadDocumentHtml,
    downloadIntV1AsWord,
    getDocumentIntV1,
    viewIntV1Json,
    getPrintPdfPresignedUrl,
    checkPrintPdfExists,
    checkPrintPdfExistsBatch,
    downloadPrintPdf,
    createTag,
    getTags,
    getTagsByQuery,
    updateTag,
    deleteTag,
    createImage,
    updateImage,
    bulkUpdateImages,
    deleteImage,
    getImagesBySearch,
    deleteImagesByTagIds,
    deleteImages,
    reassignTags,
    reassign,
    getImageCategories,
    getLxFiles,
    getRecentlyCreatedFiles,
    getLxFilePublishedBy,
    generateLxFileEmbedding,
    deleteLxFile,
    updateLxFile,
    uploadLxFile,
    uploadFile,
    documentChecker,
    setImageCategory,
    generateQuestions,
  }

  return (
    <ApiContext.Provider value={providerValue}>{children}</ApiContext.Provider>
  )
}
